import { useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { toast } from "sonner";
import {
  DotsSixVerticalIcon, TrashIcon, FloppyDiskIcon, PlusIcon, CaretDownIcon, TagIcon,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import HubPreview from "@/components/HubPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BG_OPTIONS = [
  { id: "dark", swatch: "#050505" },
  { id: "green", swatch: "#031a10" },
  { id: "light", swatch: "#f4f4f2" },
  { id: "violet", swatch: "#0d0620" },
];
const UTM_FIELDS = ["source", "medium", "campaign", "term", "content"];

function LinkCard({ link, onChange, onSave, onDelete, onToggle }) {
  const controls = useDragControls();
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange({ ...link, ...patch });
  const setUtm = (k, v) => onChange({ ...link, utm: { ...(link.utm || {}), [k]: v } });

  return (
    <Reorder.Item value={link} dragListener={false} dragControls={controls}
      className="border border-border bg-card" data-testid={`link-card-${link.id}`}>
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="cursor-grab active:cursor-grabbing text-white/30 hover:text-white/70 transition-colors touch-none"
          data-testid={`link-drag-${link.id}`}
        >
          <DotsSixVerticalIcon weight="bold" size={20} />
        </button>
        <div className="flex-1 min-w-0 space-y-2">
          <Input data-testid={`link-title-${link.id}`} value={link.title} onChange={(e) => set({ title: e.target.value })} placeholder="Title" className="h-9 bg-secondary/40 border-border text-sm focus-visible:ring-primary" />
          <Input data-testid={`link-url-${link.id}`} value={link.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" className="h-9 bg-secondary/40 border-border font-mono text-xs focus-visible:ring-primary" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <Switch data-testid={`link-enabled-${link.id}`} checked={link.enabled !== false} onCheckedChange={(v) => onToggle({ ...link, enabled: v })} />
          <button onClick={() => onDelete(link)} data-testid={`link-delete-${link.id}`} className="text-white/30 hover:text-destructive transition-colors"><TrashIcon weight="bold" /></button>
        </div>
      </div>
      <div className="border-t border-border">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-white/50 hover:text-white transition-colors" data-testid={`link-advanced-${link.id}`}>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Affiliate & tracking</span>
          <CaretDownIcon className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="px-3 pb-3 space-y-3">
            <div className="flex items-center justify-between border border-border p-2.5">
              <div>
                <p className="text-xs font-bold">Macro substitution</p>
                <p className="font-mono text-[10px] text-white/40">{"{tag} {scan} {country} {ts}"} replaced at redirect</p>
              </div>
              <Switch data-testid={`link-macros-${link.id}`} checked={!!link.macros_enabled} onCheckedChange={(v) => set({ macros_enabled: v })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {UTM_FIELDS.map((f) => (
                <Input key={f} data-testid={`link-utm-${f}-${link.id}`} value={(link.utm || {})[f] || ""} onChange={(e) => setUtm(f, e.target.value)} placeholder={`utm_${f}`} className="h-8 bg-secondary/40 border-border font-mono text-[11px] focus-visible:ring-primary" />
              ))}
            </div>
            <p className="font-mono text-[10px] text-white/40">UTM params append without touching existing query strings. Affiliate URLs are otherwise stored untouched.</p>
          </div>
        )}
      </div>
      <div className="border-t border-border p-2 flex justify-end">
        <Button size="sm" data-testid={`link-save-${link.id}`} onClick={() => onSave(link)} className="h-8 bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors">
          <FloppyDiskIcon weight="bold" className="mr-1" /> SAVE
        </Button>
      </div>
    </Reorder.Item>
  );
}

export default function HubEditor({ tagId, theme, links, setTheme, setLinks, reload }) {
  const [newTitle, setNewTitle] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const setT = (patch) => setTheme({ ...theme, ...patch });

  const saveTheme = async () => {
    try { await api.put(`/tags/${tagId}`, { hub_theme: theme }); toast.success("Theme saved"); }
    catch { toast.error("Could not save theme"); }
  };

  const saveLink = async (link) => {
    try {
      await api.put(`/links/${link.id}`, {
        title: link.title, url: link.url, enabled: link.enabled !== false,
        macros_enabled: !!link.macros_enabled, utm: link.utm || {},
      });
      toast.success("Link saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save link"); }
  };

  const toggleEnabled = async (link) => {
    setLinks(links.map((l) => (l.id === link.id ? link : l)));
    try { await api.put(`/links/${link.id}`, { title: link.title, url: link.url, enabled: link.enabled !== false, macros_enabled: !!link.macros_enabled, utm: link.utm || {} }); }
    catch { toast.error("Could not update"); }
  };

  const deleteLink = async (link) => {
    try { await api.delete(`/links/${link.id}`); setLinks(links.filter((l) => l.id !== link.id)); }
    catch { toast.error("Could not delete"); }
  };

  const addLink = async () => {
    if (!newTitle.trim() || !newUrl.trim()) return toast.error("Title and URL required");
    try {
      const { data } = await api.post(`/tags/${tagId}/links`, { title: newTitle.trim(), url: newUrl.trim() });
      setLinks([...links, data]);
      setNewTitle(""); setNewUrl("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add link"); }
  };

  const onReorder = (ordered) => setLinks(ordered);
  const commitReorder = async () => {
    try { await api.put(`/tags/${tagId}/links/reorder`, { order: links.map((l) => l.id) }); }
    catch { toast.error("Could not save order"); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8">
      <div className="space-y-8">
        {/* Theme */}
        <div className="border border-border bg-card p-6 space-y-5" data-testid="hub-theme-panel">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">// Hub theming</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Display name</label>
              <Input data-testid="theme-name" value={theme.display_name || ""} onChange={(e) => setT({ display_name: e.target.value })} className="bg-secondary/40 border-border focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Avatar URL</label>
              <Input data-testid="theme-avatar" value={theme.avatar_url || ""} onChange={(e) => setT({ avatar_url: e.target.value })} placeholder="https://…" className="bg-secondary/40 border-border font-mono text-xs focus-visible:ring-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Bio</label>
            <Textarea data-testid="theme-bio" value={theme.bio || ""} onChange={(e) => setT({ bio: e.target.value })} rows={2} className="bg-secondary/40 border-border resize-none focus-visible:ring-primary" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Background</label>
              <div className="flex gap-2">
                {BG_OPTIONS.map((b) => (
                  <button key={b.id} data-testid={`theme-bg-${b.id}`} onClick={() => setT({ background: b.id })}
                    className={`h-9 w-9 border-2 transition-colors ${theme.background === b.id ? "border-primary" : "border-border"}`}
                    style={{ background: b.swatch }} title={b.id} />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Button style</label>
              <Select value={theme.button_style || "solid"} onValueChange={(v) => setT({ button_style: v })}>
                <SelectTrigger data-testid="theme-btnstyle" className="bg-secondary/40 border-border focus:ring-primary"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="solid">Solid</SelectItem>
                  <SelectItem value="outline">Outline</SelectItem>
                  <SelectItem value="pill">Pill</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between border border-border p-3">
            <div>
              <p className="text-sm font-bold flex items-center gap-1.5"><TagIcon weight="bold" /> Affiliate disclosure</p>
              <p className="font-mono text-[10px] text-white/40">FTC/ASA line shown on the hub page</p>
            </div>
            <Switch data-testid="theme-disclosure" checked={!!theme.affiliate_disclosure} onCheckedChange={(v) => setT({ affiliate_disclosure: v })} />
          </div>
          {theme.affiliate_disclosure && (
            <Input data-testid="theme-disclosure-text" value={theme.disclosure_text || ""} onChange={(e) => setT({ disclosure_text: e.target.value })} placeholder="Some links may be affiliate links…" className="bg-secondary/40 border-border text-sm focus-visible:ring-primary" />
          )}
          <Button data-testid="theme-save" onClick={saveTheme} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors">
            <FloppyDiskIcon weight="bold" className="mr-1.5" /> SAVE THEME
          </Button>
        </div>

        {/* Links */}
        <div className="border border-border bg-card p-6" data-testid="hub-links-panel">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">// Links · drag to reorder</p>
          <Reorder.Group axis="y" values={links} onReorder={onReorder} className="space-y-3">
            {links.map((l) => (
              <div key={l.id} onPointerUp={commitReorder}>
                <LinkCard link={l} onChange={(nl) => setLinks(links.map((x) => (x.id === nl.id ? nl : x)))} onSave={saveLink} onDelete={deleteLink} onToggle={toggleEnabled} />
              </div>
            ))}
          </Reorder.Group>
          <div className="mt-4 space-y-2 border-t border-border pt-4">
            <Input data-testid="new-link-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New link title" className="bg-secondary/40 border-border text-sm focus-visible:ring-primary" />
            <Input data-testid="new-link-url" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://…" className="bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
            <Button data-testid="new-link-add" onClick={addLink} variant="outline" className="w-full border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs">
              <PlusIcon weight="bold" className="mr-1.5" /> ADD LINK
            </Button>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-24 self-start">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4 text-center">// Live preview</p>
        <HubPreview theme={theme} links={links} />
      </div>
    </div>
  );
}
